import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "saved_alert_ids";
const EVENT = "saved-alerts-changed";

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * Alerts the user bookmarked, kept on this device (the bookmark button on
 * alert cards and rows). Every hook instance stays in sync.
 */
export function useSavedAlerts() {
  const [saved, setSaved] = useState<Set<string>>(read);

  useEffect(() => {
    const sync = () => setSaved(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((alertId: string) => {
    const next = read();
    if (next.has(alertId)) next.delete(alertId);
    else next.add(alertId);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      // Storage full or blocked: keep the in-memory state only.
    }
    setSaved(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return { saved, isSaved: (id: string) => saved.has(id), toggle };
}
